import Markdoc, { type RenderableTreeNode } from '@markdoc/markdoc';
import React, { type ReactNode, use } from 'react';

import { useDocumentHotReload } from '@shared/docs/hooks';
import { useDocuments } from '@shared/docs/provider';
import type { DocumentPage } from '@shared/docs/types';

import css from './styles.module.css';

/**
 *	Looks a page up in the app's manifest, by canonical URL
 *	(`/docs/getting-started`) or by source path, which leads with its collection
 *	(`docs/README.md`, `~/docs/README.md`, `design/atoms/button/README.md`).
 *	Returns the page's metadata — title, version, source — without loading it.
 */
export function useDocument(page: string): Optional<DocumentPage> {
	const { runtime } = useDocuments();

	return runtime.resolve(page);
}

interface DocumentProps {
	page: string;

	className?: string;
	fallback?: ReactNode;
}

/**
 *	Renders one documentation page. Suspends while its source is read and
 *	transformed, and renders `fallback` when the reference matches no page.
 *
 *	`className` lands on the page's own root element — the `<article>` Markdoc
 *	renders a document as — rather than on a wrapper around it, alongside the
 *	default typography.
 */
export function Document({
	page,

	className,
	fallback = null,
}: DocumentProps): ReactNode {
	const revision = useDocumentHotReload();
	const resolved = useDocument(page);

	if (!resolved) {
		return fallback;
	}

	// Keyed by URL so navigating between pages remounts rather than reusing the
	// previous page's suspended state. The revision is part of the key so a hot
	// update re-runs `use()` against the freshly invalidated cache.
	return (
		<DocumentContent
			key={`${resolved.url}:${revision}`}
			page={resolved}
			className={className}
		/>
	);
}

interface DocumentContentProps {
	page: DocumentPage;

	className?: string;
}

/**
 *	Suspends on the page's transform, then renders it. Split out so the `use()`
 *	boundary sits below the resolution logic — a miss must fall back rather than
 *	suspend.
 */
function DocumentContent({ page, className }: DocumentContentProps): ReactNode {
	const { runtime, config, components } = useDocuments();

	const content = use(runtime.load(page, config));

	return Markdoc.renderers.react(withClassName(content, className), React, {
		components,
	}) as ReactNode;
}

/**
 *	Puts the default typography on the root tag, and the caller's class after it
 *	so an app's rules are the ones written last.
 *
 *	Copies the tag rather than writing to it: the transform behind `content` is
 *	memoized per page, so an assignment would leak one <Document>'s class onto
 *	every other rendering of the same page.
 */
function withClassName(
	content: RenderableTreeNode,
	className: Optional<string>
): RenderableTreeNode {
	if (!Markdoc.Tag.isTag(content)) {
		return content;
	}

	const merged = [css.document, content.attributes['class'], className]
		.filter(Boolean)
		.join(' ');

	return new Markdoc.Tag(
		content.name,
		{ ...content.attributes, class: merged },
		content.children
	);
}
